import React from 'react';
import { StyleSheet, View, Text, TouchableOpacity } from 'react-native';
import { createBottomTabNavigator, BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { MainTabParamList } from './types';
import { useAppColors } from '../hooks/useAppColors';
import { responsiveContentStyle } from '../hooks/useResponsiveLayout';
import { useTranslation } from 'react-i18next';
import { checkLegalStatus } from '../utils/checkLegalStatus';
import CheckInScreen from '../screens/home/CheckInScreen';
import ClassesScreen from '../screens/classes/ClassesScreen';
import TrainingScreen from '../screens/training/TrainingScreen';
import ProfileScreen from '../screens/profile/ProfileScreen';
import PendingVerificationBanner from '../components/common/PendingVerificationBanner';
import PickupSetupPrompt, { usePickupSetupPrompt } from '../components/common/PickupSetupPrompt';

const Tab = createBottomTabNavigator<MainTabParamList>();

const TAB_CONFIG: Record<string, { icon: string; labelKey: string }> = {
  Home: { icon: 'home', labelKey: 'tabs.home' },
  Classes: { icon: 'calendar-month', labelKey: 'tabs.classes' },
  Training: { icon: 'trophy', labelKey: 'tabs.training' },
  Profile: { icon: 'account', labelKey: 'tabs.profile' },
};

function CustomTabBar({ state, descriptors, navigation }: BottomTabBarProps) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const insets = useSafeAreaInsets();

  return (
    <View
      style={[
        styles.tabBar,
        {
          backgroundColor: colors.tabBarBg,
          borderTopColor: colors.border,
          paddingBottom: Math.max(insets.bottom, 16),
        },
      ]}
    >
      <View style={[styles.tabBarInner, responsiveContentStyle]}>
      {state.routes.map((route, index) => {
        const isFocused = state.index === index;
        const config = TAB_CONFIG[route.name] || { icon: 'circle', labelKey: route.name };

        const onPress = () => {
          const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
            canPreventDefault: true,
          });
          if (!isFocused && !event.defaultPrevented) {
            navigation.navigate(route.name);
          }
        };

        return (
          <TouchableOpacity
            key={route.key}
            onPress={onPress}
            style={styles.tabItem}
            activeOpacity={0.7}
          >
            <Icon
              name={config.icon}
              size={22}
              color={isFocused ? colors.tabActive : colors.tabInactive}
            />
            <Text
              style={[
                styles.tabLabel,
                { color: isFocused ? colors.tabActive : colors.tabInactive },
              ]}
            >
              {t(config.labelKey)}
            </Text>
          </TouchableOpacity>
        );
      })}
      </View>
    </View>
  );
}

export default function MainTabNavigator() {
  const { shouldShow, handleSetupNow, handleMaybeLater } = usePickupSetupPrompt();
  
  // Re-validate legal/sub-user gating every time the authenticated app is
  // presented — whether reached via login, OAuth callback, subscription
  // completion, or a stack reset from another flow. This is the single source
  // of truth so individual flows never need to call checkLegalStatus() manually.
  useFocusEffect(
    React.useCallback(() => {
      checkLegalStatus();
    }, [])
  );

  return (
    <View style={{ flex: 1 }}>
      <PendingVerificationBanner />
      <PickupSetupPrompt
        visible={shouldShow}
        onDismiss={handleMaybeLater}
        onSetupNow={handleSetupNow}
      />
      <Tab.Navigator
        tabBar={(props) => <CustomTabBar {...props} />}
        screenOptions={{ headerShown: false }}
      >
        <Tab.Screen name="Home" component={CheckInScreen} />
        <Tab.Screen name="Classes" component={ClassesScreen} />
        <Tab.Screen name="Training" component={TrainingScreen} />
        <Tab.Screen name="Profile" component={ProfileScreen} />
      </Tab.Navigator>
    </View>
  );
}

const styles = StyleSheet.create({
  tabBar: {
    borderTopWidth: 1,
  },
  tabBarInner: {
    flexDirection: 'row',
    paddingTop: 12,
    paddingHorizontal: 24,
    justifyContent: 'space-between',
  },
  tabItem: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    flex: 1,
  },
  tabLabel: {
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
});
